import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  background as backgroundColor,
  text as textColor,
  border as borderColor,
} from 'theme/palette'
import Type from 'components/Type'
import Icon from 'components/Icon'
import Box from '@mui/material/Box'
import { borderWidth } from 'theme/globals'
import { useUniqueID } from 'utils/hooks'

const SIZES = {
  sm: { size: '16px', label: 'dataSmall', checkmark: 10 },
  md: { size: '21px', label: 'dataSmall', checkmark: 13 },
  lg: { size: '25px', label: 'dataSmall', checkmark: 15 },
}

const Input = styled.input.withConfig({
  shouldForwardProp: prop => ![
    'size',
    'label',
    'inactiveColor',
    'borderWidth',
    'borderRadius',
    'borderColor',
    'variant',
    'color',
    'borderActiveColor',
    'hoverColor',
    'align',
  ].includes(prop),
})`
  & + label {
    ${props => props.theme.typography[SIZES[props.size].label]}
    display: flex;
    align-items: center;
    min-height: ${props => SIZES[props.size].size};
    transition: ${props =>
      props.theme.internalMixins.setTransition(['opacity'])};
  }
  &:checked,
  &:not(:checked) {
    position: absolute;
    left: -9999px;
  }
  &:checked + label,
  &:not(:checked) + label {
    position: relative;
    padding-left: ${props =>
      `calc(${SIZES[props.size].size} + ${props.label ? '10px' : '0px'})`};
    cursor: pointer;
    display: inline-flex;
    user-select: none;
    // color: inherit;
  }
  &:checked + label:before,
  &:not(:checked) + label:before {
    content: '';
    position: absolute;
    top: ${props => props.align === 'top' ? '2px' : '0'};
    bottom: ${props => props.align === 'top' ? 'initial' : '0'};
    left: 0;
    margin: auto;
    width: ${props => SIZES[props.size].size};
    height: ${props => SIZES[props.size].size};
    background-color: ${props =>
      props.theme.palette.background[props.inactiveColor].main};
    border-style: solid;
    border-width: ${props =>
      props.theme.globals.borderWidth[props.borderWidth]};
    border-color: ${props => props.theme.palette.border[props.borderColor]};
    border-radius: ${props => props.theme.globals.borderRadius.main}px;
    transition: ${props => props.theme.state.transition.main};

    ${props =>
      props.variant === 'circle'
        ? `
      border-radius: ${SIZES[props.size].size};
    `
        : ''}
  }
  &:not(:checked) + label:hover:before,
  &:not(:checked):focus + label:before {
    background-color: ${props =>
      props.theme.palette.background[props.hoverColor].main};
  }
  + label + div {
    transition: ${props => props.theme.state.transition.main};
  }
  &:not(:checked) + label + div {
    opacity: 0;
  }
  &:checked + label:before {
    background-color: ${props => props.theme.palette.background[props.color].main};
    border-color: ${props => props.theme.palette.background[props.color].main};
  }
  &:checked + label:after,
  &:not(:checked) + label:after {
    content: '';
    color: ${props => props.theme.palette.background[props.color].text};
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${props => SIZES[props.size].size};
    height: 100%;
    position: absolute;
    top: ${props => props.align === 'top' ? '2px' : '0'};
    bottom: ${props => props.align === 'top' ? 'initial' : '0'};
    left: 1px;
    margin: auto;
    transition: ${props => props.theme.state.transition.main};
  }
  &[disabled] + label:before {
    background: ${props => props.theme.palette.status.disabled} !important;
    cursor: default;
  }
  &[disabled] + label:before {
    border: ${props => props.theme.globals.border.disabled} !important;
  }
  &:not(:checked) + label:after {
    opacity: 0;
    transform: scale(0);
  }
  &:checked + label:after {
    opacity: 1;
    transform: scale(1);
  }
`

const Checkbox = ({
  id,
  name,
  label,
  color,
  hoverColor,
  inactiveColor,
  variant,
  value,
  size,
  checked,
  onChange,
  labelColor,
  borderWidth,
  borderColor,
  align,
  ...props
}) => {
  const generatedUniqueID = useUniqueID()
  const uniqueID = id || generatedUniqueID

  return (
    <Box 
      display="grid" 
      alignItems="center" 
      position="relative" 
      {...props}
    >
      <Input
        type="checkbox"
        id={uniqueID}
        name={name}
        color={color}
        hoverColor={hoverColor}
        inactiveColor={inactiveColor}
        borderColor={borderColor}
        variant={variant}
        value={value}
        size={size}
        borderWidth={borderWidth}
        label={label}
        align={align}
        checked={checked}
        onChange={onChange}
        {...props}
      />
      <Type
        as="label"
        htmlFor={uniqueID}
        color={labelColor}
      >
        <span style={{ opacity: labelColor === 'secondary' ? .7 : undefined }}>{label}</span>
      </Type>
      <Box
        position="absolute"
        top={align === 'top' ? '5px' : '0'}
        bottom={align === 'top' ? 'initial' : '0'}
        left="0"
        my="auto"
        display="inline-grid"
        justifyContent="center"
        alignItems="center"
        alignContent="center"
        width={SIZES[size].size}
        sx={{
          pointerEvents: 'none',
        }}
      >
        <Icon
          icon="Checkmark"
          color="inverse"
          height={SIZES[size].checkmark - (variant === 'circle' ? 2 : 0)}
        />
      </Box>
    </Box>
  )
}

Checkbox.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  color: PropTypes.oneOf(Object.keys(backgroundColor)),
  hoverColor: PropTypes.oneOf(Object.keys(backgroundColor)),
  inactiveColor: PropTypes.oneOf(Object.keys(backgroundColor)),
  labelColor: PropTypes.oneOf(Object.keys(textColor)),
  borderWidth: PropTypes.oneOf(Object.keys(borderWidth)),
  borderColor: PropTypes.oneOf(Object.keys(borderColor)),
  variant: PropTypes.oneOf(['default', 'circle']),
  align: PropTypes.oneOf(['center', 'top']),
  size: PropTypes.oneOf(Object.keys(SIZES)),
  value: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
}

Checkbox.defaultProps = {
  color: 'secondary',
  hoverColor: 'neutralCool',
  inactiveColor: 'paper',
  borderWidth: 'main',
  borderColor: 'neutralCool_700',
  variant: 'default',
  size: 'sm',
  align: 'center',
}

export default Checkbox
